import { useMemo } from 'react';
import { theme } from '@/analyses/_shared/theme';
import { COUNTRIES, type CountryMeta } from '@/data/countries';
import { REGIONS, type Region } from '@/data/regions';
import { TIER_CONFIG } from '@/data/tiers';

export type SearchFilterProps = {
  query: string;
  onQueryChange: (q: string) => void;
  onSelectCountry: (name: string) => void;
};

const MAX_SUGGESTIONS = 8;

type Suggestion =
  | { kind: 'country'; country: CountryMeta }
  | { kind: 'region'; region: Region };

export const SearchFilter = ({
  query,
  onQueryChange,
  onSelectCountry,
}: SearchFilterProps) => {
  const suggestions = useMemo<Suggestion[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];

    // Regions first (there are only a handful, and they're a "power user" shortcut)
    const regions: Suggestion[] = Object.values(REGIONS)
      .filter(
        (r) =>
          r.name.toLowerCase().includes(q) || r.id.toLowerCase().includes(q),
      )
      .map((r) => ({ kind: 'region' as const, region: r }));

    const countries: Suggestion[] = COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.iso2.toLowerCase() === q ||
        c.iso3.toLowerCase() === q,
    ).map((c) => ({ kind: 'country' as const, country: c }));

    return [...regions, ...countries].slice(0, MAX_SUGGESTIONS);
  }, [query]);

  return (
    <div style={{ position: 'relative', width: 280 }}>
      <input
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder="Search country or region…"
        style={{
          width: '100%',
          background: theme.CARD,
          border: `1px solid ${theme.BORDER}`,
          borderRadius: 8,
          padding: '9px 14px',
          color: theme.TEXT,
          fontSize: 13,
          fontFamily: 'inherit',
          outline: 'none',
          transition: 'border-color 0.15s, box-shadow 0.15s',
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = theme.BLUE;
          e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.BLUE}22`;
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = theme.BORDER;
          e.currentTarget.style.boxShadow = 'none';
        }}
      />
      {suggestions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 40,
            left: 0,
            right: 0,
            background: theme.CARD,
            border: `1px solid ${theme.BORDER}`,
            borderRadius: 8,
            overflow: 'hidden',
            zIndex: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
          }}
        >
          {suggestions.map((s) => {
            if (s.kind === 'region') {
              const r = s.region;
              const hasAnalysis = Boolean(r.analysisKey);
              return (
                <div
                  key={`region-${r.id}`}
                  onClick={() => {
                    if (!hasAnalysis) return;
                    onSelectCountry(r.name);
                    onQueryChange('');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 12px',
                    cursor: hasAnalysis ? 'pointer' : 'default',
                    borderBottom: `1px solid ${theme.BORDER}`,
                    fontSize: 13,
                    color: hasAnalysis ? theme.TEXT : theme.MUTED,
                    opacity: hasAnalysis ? 1 : 0.6,
                  }}
                  onMouseEnter={(e) => {
                    if (hasAnalysis)
                      e.currentTarget.style.background = theme.DARK2;
                  }}
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = 'transparent')
                  }
                  title={
                    hasAnalysis
                      ? 'Open regional analysis'
                      : 'No regional analysis yet'
                  }
                >
                  <span
                    style={{
                      background: theme.BLUE,
                      color: theme.WHITE,
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      padding: '2px 6px',
                      borderRadius: 3,
                      textTransform: 'uppercase',
                    }}
                  >
                    Region
                  </span>
                  <span style={{ flex: 1 }}>{r.name}</span>
                  <span style={{ color: theme.MUTED, fontSize: 11 }}>
                    {r.memberCountries.length} countries
                  </span>
                </div>
              );
            }

            const c = s.country;
            const cfg = TIER_CONFIG[c.tier];
            return (
              <div
                key={`country-${c.iso2}`}
                onClick={() => {
                  onSelectCountry(c.name);
                  onQueryChange('');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  cursor: 'pointer',
                  borderBottom: `1px solid ${theme.BORDER}`,
                  fontSize: 13,
                  color: theme.TEXT,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = theme.DARK2)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = 'transparent')
                }
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: cfg.color,
                  }}
                />
                <span style={{ flex: 1 }}>{c.name}</span>
                <span style={{ color: theme.MUTED, fontSize: 11 }}>
                  {c.iso2}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
